import type { ShipmentDetailsDto, ShipmentLineInput } from '@/api';

import { formatDecimalInput } from '../../../lib/format';

/*
 * Черновик формы отгрузки: то, что менеджер набирает до сохранения. Числа
 * хранятся строками - так поле не спорит с пользователем посреди ввода
 * («12,» ещё не число), - и разбираются при расчёте суммы и отправке.
 */

/** Клиент в поле выбора: из поиска партнёров или из карточки отгрузки. */
export interface PartnerOption {
  id: string;
  name: string;
  code?: string;
}

/** Продукт в поле выбора строки. */
export interface ProductOption {
  id: string;
  code: string;
  name: string;
  /** Единица хранения из 1С - подпись у количества; null - не указана. */
  unitName?: string | null;
}

export interface DraftLine {
  /** Ключ строки в React: строки добавляются и удаляются в середине. */
  key: string;
  product: ProductOption | null;
  quantity: string;
  price: string;
  /** Через сколько дней ждём следующую отгрузку товара; '' - не задано. */
  interval: string;
}

export interface ShipmentDraft {
  partner: PartnerOption | null;
  /** Дата в формате поля <input type="date">: 2026-10-15. */
  plannedDate: string;
  /**
   * Склад отправки, выбранный менеджером; '' - выбрал «не выбран». null - ещё не
   * выбирал: тогда форма подставляет склад прошлого заказа клиента (resolveWarehouseId).
   */
  warehouseId: string | null;
  /**
   * Договор клиента, выбранный менеджером; '' - выбрал «не выбран». null - ещё не
   * выбирал: тогда форма подставляет подсказку (resolveContractId). Смена клиента
   * сбрасывает выбор: договор - клиента.
   */
  contractId: string | null;
  /**
   * Адрес доставки, введённый менеджером; '' - стёр. null - ещё не трогал: тогда
   * форма подставляет адрес по умолчанию (resolveDeliveryAddress).
   */
  deliveryAddress: string | null;
  comment: string;
  lines: ReadonlyArray<DraftLine>;
}

/** Длина адреса доставки - как у `АдресДоставки` заказа 1С, куда он уйдёт. */
export const DELIVERY_ADDRESS_MAX_LENGTH = 500;

/** Через сколько дней от сегодня план по умолчанию - как в макете (todayPlus(45)). */
const DEFAULT_PLAN_OFFSET_DAYS = 45;

let lineSequence = 0;

export const newLine = (patch: Partial<DraftLine> = {}): DraftLine => {
  lineSequence += 1;

  return {
    key: `line-${lineSequence}`,
    product: null,
    quantity: '',
    price: '',
    interval: '',
    ...patch,
  };
};

/** Местная дата в формате поля даты; toISOString дал бы дату по UTC. */
const isoDate = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

export const emptyDraft = (): ShipmentDraft => {
  const planned = new Date();
  planned.setDate(planned.getDate() + DEFAULT_PLAN_OFFSET_DAYS);

  return {
    partner: null,
    plannedDate: isoDate(planned),
    warehouseId: null,
    contractId: null,
    deliveryAddress: null,
    comment: '',
    lines: [newLine()],
  };
};

export const draftFromShipment = (
  shipment: ShipmentDetailsDto,
): ShipmentDraft => ({
  partner: { id: shipment.partnerId, name: shipment.partnerName },
  plannedDate: shipment.plannedDate,
  warehouseId: shipment.warehouseId ?? null,
  contractId: shipment.contractId ?? null,
  deliveryAddress: shipment.deliveryAddress ?? null,
  comment: shipment.comment ?? '',
  lines:
    shipment.lines.length === 0
      ? [newLine()]
      : shipment.lines.map((line) =>
          newLine({
            product: {
              id: line.productId,
              code: line.productCode,
              name: line.productName,
              unitName: line.unitName,
            },
            quantity: formatDecimalInput(line.quantity),
            price:
              line.price === null || line.price === undefined
                ? ''
                : formatDecimalInput(line.price),
            interval:
              line.nextShipmentIntervalDays === null ||
              line.nextShipmentIntervalDays === undefined
                ? ''
                : String(line.nextShipmentIntervalDays),
          }),
        ),
});

/**
 * Интервал из поля ввода: целое число дней больше нуля. Пустое поле - null,
 * остальное - NaN. Верхний предел (два года) проверяет сервер.
 */
export const parseInterval = (text: string): number | null => {
  const normalized = text.trim();
  if (normalized === '') return null;

  const days = Number(normalized);

  return Number.isInteger(days) && days > 0 ? days : Number.NaN;
};

/**
 * Число из поля ввода: пробелы убираются, запятая - десятичный разделитель.
 * Пустое поле - null, мусор - NaN.
 */
export const parseDecimal = (text: string): number | null => {
  const normalized = text.replace(/\s/g, '').replace(',', '.');

  return normalized === '' ? null : Number(normalized);
};

/**
 * Сумма строки до копеек - так же, как бэкенд: половина копейки округляется от
 * нуля. Считается в целых (тысячные доли количества на копейки цены): в двоичной
 * дроби 25,5 × 154,17 = 3931,33499..., и Math.round дал бы на копейку меньше
 * сервера. null - цены или количества нет.
 */
export const lineAmount = (line: DraftLine): number | null => {
  const quantity = parseDecimal(line.quantity);
  const price = parseDecimal(line.price);

  if (quantity === null || price === null) return null;
  if (Number.isNaN(quantity) || Number.isNaN(price)) return null;

  const scaled = Math.round(quantity * 1000) * Math.round(price * 100);

  return Math.round(scaled / 1000) / 100;
};

const isBlank = (line: DraftLine): boolean =>
  line.product === null &&
  line.quantity.trim() === '' &&
  line.price.trim() === '' &&
  line.interval.trim() === '';

/** Строки, которые уйдут на сервер: пустые строки формы молча отбрасываются. */
export const filledLines = (draft: ShipmentDraft): ReadonlyArray<DraftLine> =>
  draft.lines.filter((line) => !isBlank(line));

/**
 * Что мешает сохранить - словами для менеджера. Проверки те же, что на сервере,
 * но на месте: не ждать 400 ради пропущенного клиента. Правило «передать в 1С
 * можно только с ценой» здесь не проверяется - черновик без цены сохраняется.
 */
export const validateDraft = (draft: ShipmentDraft): ReadonlyArray<string> => {
  const problems: string[] = [];

  if (draft.partner === null) problems.push('Выберите клиента.');
  if (draft.plannedDate === '') problems.push('Укажите плановую дату.');

  filledLines(draft).forEach((line, index) => {
    const row = `Позиция ${index + 1}`;
    const quantity = parseDecimal(line.quantity);
    const price = parseDecimal(line.price);

    if (line.product === null) problems.push(`${row}: выберите товар.`);

    if (quantity === null || Number.isNaN(quantity) || quantity <= 0)
      problems.push(`${row}: количество должно быть больше нуля.`);

    if (price !== null && (Number.isNaN(price) || price < 0))
      problems.push(`${row}: цена должна быть числом не меньше нуля.`);

    if (Number.isNaN(parseInterval(line.interval)))
      problems.push(
        `${row}: интервал до следующей отгрузки - целое число дней больше нуля.`,
      );
  });

  return problems;
};

/**
 * Склад, который уйдёт на сервер: выбор менеджера, а если он не выбирал -
 * подсказка (склад прошлого заказа клиента). '' - склада нет.
 */
export const resolveWarehouseId = (
  draft: ShipmentDraft,
  hintWarehouseId: string | null | undefined,
): string => draft.warehouseId ?? hintWarehouseId ?? '';

/**
 * Договор, который уйдёт на сервер: выбор менеджера, а если он не выбирал -
 * подсказка (договор прошлого заказа клиента с этого склада, прошлого заказа или
 * единственный). '' - договора нет.
 */
export const resolveContractId = (
  draft: ShipmentDraft,
  hintContractId: string | null | undefined,
): string => draft.contractId ?? hintContractId ?? '';

/**
 * Адрес, который уйдёт на сервер: ввод менеджера, а если он поле не трогал -
 * подсказка (адрес последнего заказа клиента или адрес клиента). '' - адреса нет.
 */
export const resolveDeliveryAddress = (
  draft: ShipmentDraft,
  hintAddress: string | null | undefined,
): string => draft.deliveryAddress ?? hintAddress ?? '';

/** Строки тела запроса; вызывать после validateDraft без замечаний. */
export const toRequestLines = (
  draft: ShipmentDraft,
): Array<ShipmentLineInput> =>
  filledLines(draft).map((line) => ({
    productId: line.product?.id ?? '',
    quantity: parseDecimal(line.quantity) ?? 0,
    price: parseDecimal(line.price),
    nextShipmentIntervalDays: parseInterval(line.interval),
  }));
